import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowLeft, Building, Loader2, Send, Check, CheckCheck, Phone, Video, Info,
  Paperclip, Smile, ThumbsUp, X as XIcon, FileText, Pencil, Trash2, Ban,
} from 'lucide-react';
import {
  fetchThread, insertOptimisticMessage, sendMessage, setActiveSession, reactToMessage,
  editMessage, deleteMessage,
} from '../../../store/slices/messengerSlice';
import messengerApi from '../../../api/messenger';
import messengerSocket from '../../../services/messengerSocket';

const formatHM = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatDay = (iso) => {
  const d = new Date(iso);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  if (d >= today) return 'Today';
  if (d >= yesterday) return 'Yesterday';
  return d.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'short' });
};

const sameDay = (a, b) =>
  a && b && new Date(a).toDateString() === new Date(b).toDateString();

// Quick-react palette — same default set Meta Messenger surfaces on long-press.
const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '👏'];

// Larger palette for inserting emoji into the draft message. Kept compact —
// users have OS-level pickers for the long tail; this just makes the
// "you CAN add emoji" affordance obvious without a heavy dependency.
const COMPOSER_EMOJIS = [
  '😀','😃','😄','😁','😆','😅','😂','🤣','😊','🙂','😉','😍',
  '😘','😜','🤪','🤩','🥳','😎','🤔','😴','😢','😭','😡','🤯',
  '👍','👎','👏','🙏','💪','🙌','👋','🤝','✌️','🤞','👌','🤙',
  '❤️','💔','💯','🔥','✨','🎉','🎊','🏠','🏡','🔑','💰','📅',
];

const MAX_ATTACHMENT_MB = 15;

// Messages may be edited by their sender for one hour after sending — mirrors
// the server-side MESSAGE_EDIT_WINDOW_SECONDS. After that the Edit option is
// hidden (the server would reject it anyway).
const EDIT_WINDOW_MS = 60 * 60 * 1000;

const isPersistedId = (id) => typeof id === 'number';

const canEditMessage = (m) => (
  isPersistedId(m?.id)
  && !m.is_deleted
  && m.role !== 'system'
  && !!(m.content && m.content.trim().length)
  && m.created_at
  && (Date.now() - new Date(m.created_at).getTime()) <= EDIT_WINDOW_MS
);

const canDeleteMessage = (m) => isPersistedId(m?.id) && !m.is_deleted && m.role !== 'system';

// Backend stores attachments with a relative URL (e.g. /media/...). When the
// React app is served from a different origin (dev: localhost:3000 vs Django
// 8000) the browser would resolve that against the FE origin and 404. Detect
// relative URLs and prepend the API host so <img>/<a> render correctly.
const API_HOST = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const absoluteAttachmentUrl = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url) || url.startsWith('data:') || url.startsWith('blob:')) return url;
  return `${API_HOST.replace(/\/$/, '')}${url.startsWith('/') ? '' : '/'}${url}`;
};

/**
 * Floating popup rendered via portal so it's never clipped by the message
 * scroll container's `overflow:auto`. Auto-flips above/below the anchor
 * depending on viewport space.
 */
const PortalPopup = ({ anchorRect, onClose, children, width = 240, gap = 8 }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!onClose) return undefined;
    const handle = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handle);
    document.addEventListener('touchstart', handle);
    return () => {
      document.removeEventListener('mousedown', handle);
      document.removeEventListener('touchstart', handle);
    };
  }, [onClose]);

  if (!anchorRect) return null;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // Prefer above; flip below if there's more room there.
  const spaceAbove = anchorRect.top;
  const spaceBelow = vh - anchorRect.bottom;
  const above = spaceAbove > 60 || spaceAbove > spaceBelow;
  const top = above ? Math.max(8, anchorRect.top - gap) : Math.min(vh - 8, anchorRect.bottom + gap);
  // Right-align so it doesn't overflow when triggered near the right edge.
  let left = anchorRect.left + anchorRect.width / 2 - width / 2;
  left = Math.max(8, Math.min(left, vw - width - 8));
  const style = {
    position: 'fixed',
    left,
    top,
    transform: above ? 'translateY(-100%)' : 'none',
    zIndex: 10001,
  };
  return createPortal(
    <div ref={ref} style={style}>{children}</div>,
    document.body,
  );
};

const ReactionPickerPopup = ({ anchorRect, onPick, onClose }) => (
  <PortalPopup anchorRect={anchorRect} onClose={onClose} width={240} gap={6}>
    <div className="bg-white border border-gray-200 shadow-xl rounded-full px-2 py-1.5 flex gap-1">
      {REACTION_EMOJIS.map((e) => (
        <button
          key={e}
          type="button"
          onClick={() => onPick(e)}
          className="w-9 h-9 text-xl hover:scale-125 transition-transform leading-none"
          aria-label={`React with ${e}`}
        >
          {e}
        </button>
      ))}
    </div>
  </PortalPopup>
);

const ComposerEmojiPicker = ({ anchorRect, onPick, onClose }) => (
  <PortalPopup anchorRect={anchorRect} onClose={onClose} width={300} gap={8}>
    <div className="bg-white border border-gray-200 shadow-xl rounded-xl p-2 w-[300px]">
      <div className="grid grid-cols-8 gap-0.5 max-h-56 overflow-y-auto">
        {COMPOSER_EMOJIS.map((e) => (
          <button
            key={e}
            type="button"
            onClick={() => onPick(e)}
            className="w-9 h-9 text-xl hover:bg-gray-100 rounded leading-none"
            aria-label={`Insert ${e}`}
          >
            {e}
          </button>
        ))}
      </div>
    </div>
  </PortalPopup>
);


/**
 * Right-click context menu anchored at the cursor position (clientX/clientY).
 * Rendered via portal so it escapes the message scroll container's overflow.
 * Closes on any outside click or scroll.
 */
const CursorMenu = ({ pos, onClose, children, width = 168 }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!onClose) return undefined;
    const handle = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handle);
    window.addEventListener('scroll', onClose, true);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('mousedown', handle);
      window.removeEventListener('scroll', onClose, true);
      window.removeEventListener('resize', onClose);
    };
  }, [onClose]);
  if (!pos) return null;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const left = Math.min(pos.x, vw - width - 8);
  const top = Math.min(pos.y, vh - 96);
  return createPortal(
    <div
      ref={ref}
      style={{ position: 'fixed', left: Math.max(8, left), top: Math.max(8, top), width, zIndex: 10002 }}
      className="bg-white border border-gray-200 rounded-lg shadow-xl py-1 text-sm"
    >
      {children}
    </div>,
    document.body,
  );
};

// Hard ceiling for attachment thumbnails. Kept generous enough to read at a
// glance but small enough that even the tallest portrait can't push the
// composer below the viewport.
const ATTACHMENT_CAP_PX = 220;

const AttachmentBubble = ({ message }) => {
  const url = absoluteAttachmentUrl(message.attachment_url);
  const name = message.attachment_name || 'attachment';
  const type = message.attachment_type || 'file';
  if (!url) return null;
  // Everything inline-styled so no Tailwind purge / JIT issue can un-cap the
  // size. The outer box has `overflow:hidden` so even a misbehaving inner
  // <img> is clipped to the box.
  const boxStyle = {
    width: `${ATTACHMENT_CAP_PX}px`,
    maxWidth: `${ATTACHMENT_CAP_PX}px`,
    maxHeight: `${ATTACHMENT_CAP_PX}px`,
    overflow: 'hidden',
    borderRadius: '14px',
    border: '1px solid #e5e7eb',
    display: 'block',
    flex: '0 0 auto',
    boxSizing: 'border-box',
  };
  const mediaStyle = {
    display: 'block',
    width: '100%',
    height: 'auto',
    maxWidth: '100%',
    maxHeight: `${ATTACHMENT_CAP_PX}px`,
    objectFit: 'cover',
  };
  if (type === 'image') {
    return (
      <a href={url} target="_blank" rel="noreferrer" style={boxStyle}>
        <img src={url} alt={name} style={mediaStyle} />
      </a>
    );
  }
  if (type === 'video') {
    return (
      <div style={{ ...boxStyle, background: '#000' }}>
        <video src={url} controls style={mediaStyle} />
      </div>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      style={{ maxWidth: `${ATTACHMENT_CAP_PX}px`, flex: '0 0 auto' }}
      className="flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 bg-white text-gray-800 hover:bg-gray-50 text-base"
    >
      <FileText size={16} className="text-blue-600 flex-shrink-0" />
      <span className="truncate">{name}</span>
    </a>
  );
};

const ReactionsRow = ({ reactions, currentUserId, onToggle, alignRight }) => {
  const entries = Object.entries(reactions || {}).filter(([, ids]) => (ids || []).length > 0);
  if (entries.length === 0) return null;
  return (
    <div className={`flex flex-wrap gap-1 mt-1 ${alignRight ? 'justify-end' : 'justify-start'}`}>
      {entries.map(([emoji, ids]) => {
        const mine = (ids || []).includes(currentUserId);
        return (
          <button
            key={emoji}
            type="button"
            onClick={() => onToggle(emoji)}
            className={`text-[11px] leading-none px-1.5 py-0.5 rounded-full border flex items-center gap-1 ${
              mine
                ? 'bg-blue-50 border-blue-200 text-blue-700'
                : 'bg-white border-gray-200 text-gray-600'
            }`}
            title={mine ? 'Remove reaction' : 'Toggle reaction'}
          >
            <span>{emoji}</span>
            <span>{(ids || []).length}</span>
          </button>
        );
      })}
    </div>
  );
};

const MessageBubble = ({
  m, isMe, corners, onToggleReaction, currentUserId,
  onStartEdit, onDelete, isEditing, editDraft, onEditDraftChange, onSaveEdit, onCancelEdit,
}) => {
  const [anchorRect, setAnchorRect] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const triggerRef = useRef(null);
  const editRef = useRef(null);
  const hasAttachment = !!m.attachment_url;
  const hasText = !!(m.content && m.content.length);

  const openPicker = () => {
    if (!triggerRef.current) return;
    setAnchorRect(triggerRef.current.getBoundingClientRect());
  };
  const closePicker = () => setAnchorRect(null);

  // Right-click on your own (non-deleted) message → Edit / Delete menu.
  const handleContextMenu = (e) => {
    if (!isMe || m.is_deleted || m.pending) return;
    e.preventDefault();
    setMenuPos({ x: e.clientX, y: e.clientY });
  };

  useEffect(() => {
    if (isEditing && editRef.current) {
      const el = editRef.current;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    }
  }, [isEditing]);

  // Deleted tombstone — keeps the conversation position (Messenger pattern) but
  // shows no content, attachment or reactions.
  if (m.is_deleted) {
    return (
      <div
        style={{ maxWidth: '72%' }}
        className={`flex flex-col min-w-0 ${isMe ? 'items-end' : 'items-start'}`}
      >
        <div className={`px-3.5 py-2 text-sm italic flex items-center gap-1.5 rounded-2xl border border-dashed ${
          isMe ? 'bg-gray-50 text-gray-400 border-gray-300' : 'bg-white text-gray-400 border-gray-200'
        }`}>
          <Ban size={13} />
          {isMe ? 'You removed a message' : 'This message was deleted'}
        </div>
      </div>
    );
  }

  // Inline editor — replaces the bubble while the sender is editing.
  if (isEditing) {
    return (
      <div style={{ maxWidth: '72%' }} className="flex flex-col items-end min-w-0 w-full">
        <textarea
          ref={editRef}
          rows={2}
          value={editDraft}
          onChange={(e) => onEditDraftChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSaveEdit(); }
            if (e.key === 'Escape') onCancelEdit();
          }}
          className="w-full min-w-[220px] resize-none text-sm px-3 py-2 border border-blue-300 rounded-2xl bg-white text-gray-900 outline-none focus:border-blue-500"
        />
        <div className="flex items-center gap-3 mt-1 px-1 text-[11px]">
          <button type="button" onClick={onCancelEdit} className="text-gray-500 hover:text-gray-700">
            Cancel
          </button>
          <button type="button" onClick={onSaveEdit} className="text-blue-600 font-semibold hover:text-blue-700">
            Save
          </button>
          <span className="text-gray-400">Esc to cancel · Enter to save</span>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{ maxWidth: '72%' }}
      className={`flex flex-col min-w-0 ${isMe ? 'items-end' : 'items-start'}`}
    >
      <div className={`${isMe ? 'flex-row-reverse' : ''} flex items-center gap-1.5 min-w-0 max-w-full`}>
        {/* Reaction trigger is ALWAYS visible — gives users an obvious
            affordance that every message can be reacted to, matching the
            Messenger pattern. */}
        <button
          ref={triggerRef}
          type="button"
          onClick={openPicker}
          className="w-7 h-7 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center text-blue-600 hover:bg-blue-50 flex-shrink-0"
          title="React"
          aria-label="React"
        >
          <Smile size={14} />
        </button>

        <div
          className={`flex flex-col gap-1 min-w-0 ${isMe ? 'items-end' : 'items-start'}`}
          onContextMenu={handleContextMenu}
        >
          {hasAttachment && <AttachmentBubble message={m} />}
          {hasText && (
            <div
              title={isMe ? 'Right-click to edit or delete' : undefined}
              className={`px-3.5 py-2.5 text-base leading-relaxed whitespace-pre-wrap break-words shadow-sm ${corners} ${
                isMe
                  ? `bg-blue-600 text-white${m.pending ? ' opacity-80' : ''}`
                  : 'bg-white text-gray-900 border border-gray-200'
              }`}
            >
              {m.content}
            </div>
          )}
        </div>
      </div>
      <ReactionsRow
        reactions={m.reactions}
        currentUserId={currentUserId}
        onToggle={onToggleReaction}
        alignRight={isMe}
      />
      {anchorRect && (
        <ReactionPickerPopup
          anchorRect={anchorRect}
          onPick={(e) => { onToggleReaction(e); closePicker(); }}
          onClose={closePicker}
        />
      )}
      {menuPos && (
        <CursorMenu pos={menuPos} onClose={() => setMenuPos(null)}>
          {canEditMessage(m) ? (
            <button
              type="button"
              onClick={() => { setMenuPos(null); onStartEdit(m); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-gray-700 hover:bg-gray-50"
            >
              <Pencil size={14} /> Edit
            </button>
          ) : (
            <div className="px-3 py-2 text-[11px] text-gray-400 leading-snug">
              Editing is only available for 1 hour after sending.
            </div>
          )}
          {canDeleteMessage(m) && (
            <button
              type="button"
              onClick={() => { setMenuPos(null); onDelete(m); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-red-600 hover:bg-red-50"
            >
              <Trash2 size={14} /> Delete
            </button>
          )}
        </CursorMenu>
      )}
    </div>
  );
};

const MessengerThread = ({ currentUserId }) => {
  const dispatch = useDispatch();
  const activeSessionId = useSelector((s) => s.messenger.activeSessionId);
  const thread = useSelector((s) => s.messenger.threadsBySession[activeSessionId]);
  const inboxSessions = useSelector((s) => s.messenger.sessions);
  const inboxSession = inboxSessions.find((s) => s.id === activeSessionId);
  const threadSyncToken = inboxSession
    ? `${inboxSession.last_message_at || ''}|${inboxSession.last_message_preview || ''}|${inboxSession.unread_count || 0}`
    : '';
  const peerTyping = thread?.typing || null;
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const emojiBtnRef = useRef(null);
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const [draft, setDraft] = useState('');
  const [pendingAttachment, setPendingAttachment] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  // Inline message editing — `editingId` is the message currently being edited
  // (only one at a time); `editDraft` holds its in-progress text.
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');

  const lastTypingPingRef = useRef(0);
  const stopTypingTimerRef = useRef(null);

  const pingTyping = useCallback((isTyping) => {
    if (!activeSessionId) return;
    if (messengerSocket.connected) {
      messengerSocket.sendTyping(activeSessionId, isTyping);
      return;
    }
    messengerApi.setTyping(activeSessionId, isTyping).catch(() => { /* best-effort */ });
  }, [activeSessionId]);

  useEffect(() => {
    return () => {
      if (stopTypingTimerRef.current) clearTimeout(stopTypingTimerRef.current);
      if (lastTypingPingRef.current > 0) pingTyping(false);
      lastTypingPingRef.current = 0;
    };
  }, [activeSessionId, pingTyping]);

  // Clear any pending attachment when switching threads — the file belongs to
  // the conversation the user picked it for; carrying it across is a footgun.
  useEffect(() => {
    setPendingAttachment(null);
    setAttachmentPreview(null);
    setEditingId(null);
    setEditDraft('');
  }, [activeSessionId]);

  const handleDraftChange = (value) => {
    setDraft(value);
    const nonEmpty = !!value && value.trim().length > 0;
    const now = Date.now();

    if (nonEmpty) {
      if (now - lastTypingPingRef.current > 3000) {
        pingTyping(true);
        lastTypingPingRef.current = now;
      }
      if (stopTypingTimerRef.current) clearTimeout(stopTypingTimerRef.current);
      stopTypingTimerRef.current = setTimeout(() => {
        pingTyping(false);
        lastTypingPingRef.current = 0;
      }, 4000);
    } else if (lastTypingPingRef.current > 0) {
      pingTyping(false);
      lastTypingPingRef.current = 0;
      if (stopTypingTimerRef.current) clearTimeout(stopTypingTimerRef.current);
    }
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread?.messages?.length, peerTyping?.user_id, pendingAttachment]);

  useEffect(() => {
    if (!activeSessionId || !threadSyncToken) return;
    dispatch(fetchThread(activeSessionId));
  }, [threadSyncToken, activeSessionId, dispatch]);

  if (!activeSessionId) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-gray-400 bg-white border-r border-gray-200">
        Select a conversation to start
      </div>
    );
  }

  const hasThread = Boolean(thread?.session);
  const showLoading = Boolean(activeSessionId && !hasThread);
  const session = thread?.session || inboxSession;
  const messages = thread?.messages || [];

  const handleFilePick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_ATTACHMENT_MB * 1024 * 1024) {
      alert(`File is too large. Max ${MAX_ATTACHMENT_MB} MB.`);
      return;
    }
    setPendingAttachment(file);
    if (file.type.startsWith('image/')) {
      setAttachmentPreview(URL.createObjectURL(file));
    } else {
      setAttachmentPreview(null);
    }
  };

  const clearAttachment = () => {
    setPendingAttachment(null);
    if (attachmentPreview) URL.revokeObjectURL(attachmentPreview);
    setAttachmentPreview(null);
  };

  const insertEmojiAtCursor = (emoji) => {
    const el = textareaRef.current;
    if (!el) {
      setDraft((d) => d + emoji);
      return;
    }
    const start = el.selectionStart ?? draft.length;
    const end = el.selectionEnd ?? draft.length;
    const next = draft.slice(0, start) + emoji + draft.slice(end);
    setDraft(next);
    // Restore focus + put the caret right after the inserted emoji.
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + emoji.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const handleSend = () => {
    const content = (draft || '').trim();
    if (!content && !pendingAttachment) return;
    if (!activeSessionId) return;

    const attachment = pendingAttachment;
    const previewType = attachment
      ? (attachment.type.startsWith('image/') ? 'image'
        : attachment.type.startsWith('video/') ? 'video'
        : attachment.type.startsWith('audio/') ? 'audio' : 'file')
      : '';
    const previewUrl = attachmentPreview || (attachment ? URL.createObjectURL(attachment) : '');

    const optimistic = {
      id: `pending-${Date.now()}`,
      role: 'agent',
      content,
      sender_id: currentUserId,
      created_at: new Date().toISOString(),
      pending: true,
      attachment_url: previewUrl,
      attachment_name: attachment?.name || '',
      attachment_type: previewType,
      reactions: {},
    };
    dispatch(insertOptimisticMessage({ sessionId: activeSessionId, message: optimistic }));
    setDraft('');
    setPendingAttachment(null);
    setAttachmentPreview(null);
    if (stopTypingTimerRef.current) clearTimeout(stopTypingTimerRef.current);
    lastTypingPingRef.current = 0;
    pingTyping(false);

    dispatch(sendMessage({ sessionId: activeSessionId, content, attachment }));
  };

  const handleQuickLike = () => {
    if (draft.trim() || pendingAttachment) {
      handleSend();
      return;
    }
    // Empty composer → send a like as a thumbs-up bubble (Messenger pattern).
    dispatch(sendMessage({ sessionId: activeSessionId, content: '👍' }));
  };

  const laneOf = (m) => {
    if (m.role === 'system') return 'system';
    if (m.sender_id === currentUserId || m.role === 'agent') return 'me';
    return 'them';
  };

  const handleToggleReaction = (messageId, emoji) => {
    dispatch(reactToMessage({ sessionId: activeSessionId, messageId, emoji }));
  };

  const handleStartEdit = (m) => {
    setEditingId(m.id);
    setEditDraft(m.content || '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditDraft('');
  };

  const handleSaveEdit = () => {
    const content = (editDraft || '').trim();
    const messageId = editingId;
    if (!content || messageId == null) { handleCancelEdit(); return; }
    setEditingId(null);
    setEditDraft('');
    dispatch(editMessage({ sessionId: activeSessionId, messageId, content }))
      .unwrap()
      .catch((err) => {
        // Most likely the 1-hour window lapsed between opening the menu and
        // saving — surface the server's reason and let the next poll reconcile.
        alert(err?.detail || 'Could not edit this message.');
      });
  };

  const handleDeleteMessage = (m) => {
    if (m?.id == null) return;
    if (!window.confirm('Delete this message? It will be replaced with “This message was deleted”.')) return;
    dispatch(deleteMessage({ sessionId: activeSessionId, messageId: m.id }));
  };

  // CSS grid with exactly 3 rows pins the composer to the bottom regardless of
  // message content. The middle row uses `minmax(0,1fr)` so it absorbs the
  // remaining space AND can shrink below its content (without that, big
  // bubbles would push the composer off-screen).
  // Rows: top (header + property card) / scroll / bottom (preview + composer).
  return (
    <div
      className="grid h-full min-h-0 bg-white min-w-0"
      style={{ gridTemplateRows: 'auto minmax(0, 1fr) auto' }}
    >
      {/* Row 1: header + property card */}
      <div className="min-h-0">
      <div className="px-3 py-2.5 md:px-4 md:py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <button
            type="button"
            onClick={() => dispatch(setActiveSession(null))}
            className="lg:hidden p-1 -ml-1 text-gray-700 hover:text-blue-700 flex-shrink-0"
            aria-label="Back to conversations"
          >
            <ArrowLeft size={22} />
          </button>
          <div className="relative flex-shrink-0">
            <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-semibold">
              {session?.buyer?.avatar_initials || 'GU'}
            </div>
            <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-white" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900 truncate">{session?.buyer?.name || '—'}</div>
            <div className="text-[11px] text-gray-500 truncate">
              {peerTyping ? 'Typing…' : (session?.buyer?.email || 'Active now')}
            </div>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-1 text-blue-600 flex-shrink-0">
          <button type="button" className="p-2 hover:bg-gray-100 rounded-full" aria-label="Call">
            <Phone size={18} />
          </button>
          <button type="button" className="p-2 hover:bg-gray-100 rounded-full" aria-label="Video call">
            <Video size={18} />
          </button>
          <button type="button" className="p-2 hover:bg-gray-100 rounded-full" aria-label="Conversation info">
            <Info size={18} />
          </button>
        </div>
      </div>

      {session?.property && (
        <div className="mx-4 mt-3 mb-1 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 flex gap-3 items-center">
          <div className="w-9 h-9 rounded bg-white border border-blue-100 flex items-center justify-center flex-shrink-0">
            <Building size={16} className="text-blue-700" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold text-gray-900 truncate">{session.property.address}</div>
            <div className="text-[11px] text-gray-500 truncate">
              {session.property.price && `$${Number(session.property.price).toLocaleString('en-AU', { maximumFractionDigits: 0 })}`}
              {session.property.bedrooms ? ` · ${session.property.bedrooms} bed` : ''}
              {session.property.bathrooms ? ` · ${session.property.bathrooms} bath` : ''}
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Row 2: scrollable messages — fills remaining space, scrolls internally */}
      <div ref={scrollRef} className="min-h-0 overflow-y-auto px-4 py-3 bg-gray-50">
        {showLoading ? (
          <div className="flex flex-col items-center justify-center h-full min-h-[200px] gap-2 text-gray-400">
            <Loader2 className="w-7 h-7 animate-spin text-blue-600" />
            <span className="text-sm">Loading conversation…</span>
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-xs text-gray-400 py-6">
            Say hello to {session?.buyer?.name?.split(' ')[0] || 'your buyer'}.
          </div>
        ) : null}

        {!showLoading && messages.map((m, idx) => {
          const prev = messages[idx - 1];
          const next = messages[idx + 1];
          const lane = laneOf(m);
          const prevLane = prev ? laneOf(prev) : null;
          const nextLane = next ? laneOf(next) : null;
          const showDay = !prev || !sameDay(prev.created_at, m.created_at);
          const isFirstInGroup = lane !== prevLane || showDay;
          const isLastInGroup = lane !== nextLane;

          if (lane === 'system') {
            return (
              <React.Fragment key={m.id}>
                {showDay && (
                  <div className="text-center text-[10px] text-gray-400 my-3 uppercase tracking-wider font-medium">
                    {formatDay(m.created_at)}
                  </div>
                )}
                <div className="text-center text-[10px] text-gray-400 my-1 italic">{m.content}</div>
              </React.Fragment>
            );
          }

          const isMe = lane === 'me';
          const corners = isMe
            ? `rounded-2xl ${isLastInGroup ? 'rounded-br-md' : ''}`
            : `rounded-2xl ${isLastInGroup ? 'rounded-bl-md' : ''}`;

          return (
            <React.Fragment key={m.id}>
              {showDay && (
                <div className="text-center text-[10px] text-gray-400 my-3 uppercase tracking-wider font-medium">
                  {formatDay(m.created_at)}
                </div>
              )}
              <div className={`flex ${isMe ? 'justify-end' : 'justify-start'} ${isFirstInGroup ? 'mt-2' : 'mt-0.5'}`}>
                <MessageBubble
                  m={m}
                  isMe={isMe}
                  corners={corners}
                  currentUserId={currentUserId}
                  onToggleReaction={(emoji) => handleToggleReaction(m.id, emoji)}
                  onStartEdit={handleStartEdit}
                  onDelete={handleDeleteMessage}
                  isEditing={editingId === m.id}
                  editDraft={editDraft}
                  onEditDraftChange={setEditDraft}
                  onSaveEdit={handleSaveEdit}
                  onCancelEdit={handleCancelEdit}
                />
              </div>
              {isLastInGroup && (
                <div className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                  <div className={`flex items-center gap-1 mt-1 px-1 text-[10px] text-gray-400 ${isMe ? 'flex-row-reverse' : ''}`}>
                    <span>{formatHM(m.created_at)}</span>
                    {m.edited_at && !m.is_deleted && <span className="italic">· Edited</span>}
                    {isMe && !m.is_deleted && (m.read_at
                      ? <CheckCheck size={11} className="text-blue-500" />
                      : <Check size={11} />)}
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}

        {!showLoading && peerTyping && (
          <div className="flex justify-start mt-2">
            <div className="flex items-center gap-2 bg-white text-gray-500 border border-gray-200 rounded-2xl rounded-bl-md px-3 py-2 shadow-sm">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" />
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0.1s' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0.2s' }} />
              </div>
              <span className="text-[11px] italic">
                {(peerTyping.name || 'Buyer').split(' ')[0]} is typing…
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Row 3: preview + composer (pinned to bottom by the grid) */}
      <div className="min-h-0">
      {/* Attachment preview row — Messenger shows a thumbnail above the
          composer once a file is staged so the user can confirm before sending. */}
      {pendingAttachment && (
        <div className="px-3 pt-2 flex items-center gap-2">
          <div className="relative inline-flex items-center gap-2 border border-gray-200 rounded-lg p-1.5 bg-gray-50">
            {attachmentPreview ? (
              <img src={attachmentPreview} alt="" className="w-12 h-12 object-cover rounded" />
            ) : (
              <div className="w-12 h-12 rounded bg-white border border-gray-200 flex items-center justify-center">
                <FileText size={18} className="text-blue-600" />
              </div>
            )}
            <span className="text-xs text-gray-700 max-w-[180px] truncate">
              {pendingAttachment.name}
            </span>
            <button
              type="button"
              onClick={clearAttachment}
              className="ml-1 w-5 h-5 rounded-full bg-gray-700 text-white flex items-center justify-center"
              aria-label="Remove attachment"
            >
              <XIcon size={12} />
            </button>
          </div>
        </div>
      )}

      <div
        className="px-3 py-2.5 border-t border-gray-200 flex gap-2 items-end bg-white"
        // Extra bottom padding clears the iOS home indicator / device safe area
        // so the composer is never tucked under it on mobile.
        style={{ paddingBottom: 'calc(0.625rem + env(safe-area-inset-bottom))' }}
      >
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
          onChange={handleFilePick}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="w-9 h-9 rounded-full text-blue-600 hover:bg-blue-50 flex items-center justify-center flex-shrink-0"
          title="Attach a file or image"
        >
          <Paperclip size={18} />
        </button>
        <button
          ref={emojiBtnRef}
          type="button"
          onClick={() => {
            if (emojiAnchor) setEmojiAnchor(null);
            else if (emojiBtnRef.current) setEmojiAnchor(emojiBtnRef.current.getBoundingClientRect());
          }}
          className="w-9 h-9 rounded-full text-blue-600 hover:bg-blue-50 flex items-center justify-center flex-shrink-0"
          title="Add an emoji"
          aria-label="Add an emoji"
        >
          <Smile size={18} />
        </button>
        <textarea
          ref={textareaRef}
          rows={1}
          value={draft}
          onChange={(e) => handleDraftChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder={`Message ${session?.buyer?.name?.split(' ')[0] || 'buyer'} — add 😊 with the smile button…`}
          className="flex-1 resize-none text-sm px-4 py-2 border border-gray-200 rounded-2xl bg-gray-50 text-gray-900 outline-none focus:border-blue-400 focus:bg-white max-h-32"
          style={{ minHeight: '2.25rem' }}
        />
        {draft.trim() || pendingAttachment ? (
          <button
            onClick={handleSend}
            className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 flex-shrink-0"
            title="Send"
          >
            <Send size={15} />
          </button>
        ) : (
          <button
            onClick={handleQuickLike}
            className="w-9 h-9 rounded-full text-blue-600 hover:bg-blue-50 flex items-center justify-center flex-shrink-0"
            title="Send a like"
          >
            <ThumbsUp size={18} />
          </button>
        )}
      </div>
      </div>

      {emojiAnchor && (
        <ComposerEmojiPicker
          anchorRect={emojiAnchor}
          onPick={(e) => insertEmojiAtCursor(e)}
          onClose={() => setEmojiAnchor(null)}
        />
      )}
    </div>
  );
};

export default MessengerThread;
